/**
 * SKILLS-V1 — capability catalogue, truthful target resolver, and rollout waves.
 * Board/base OTA is intentionally absent; it remains a superadmin-only concern.
 */
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico, Alert, SOFT, go } from "./ui.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { path, useApi } from "./api.js";
import { useSession } from "./session.js";
import { EdgeSkillsWarehouseDesk, skillsSeatOf } from "./edge-skills-warehouse.js";
import { CapCampaignDesk } from "./cap-campaign-desk.js";
import { ObjectCard, WorkbenchHead, PrepSpine } from "./object-workbench.js";

const TABS = [
  { id:"library", label:"Packs", verb:"Choose", say:"Choose what a board may carry." },
  { id:"assign", label:"Boards", verb:"Place", say:"Pick a board, group, or zone before rollout." },
  { id:"waves", label:"Waves", verb:"Watch", say:"Watch boards accept the pack. Sent is not accepted." },
];

function deviceRowsOf(data) {
  const rows = Array.isArray(data) ? data : (data && (data.items || data.devices)) || [];
  return Array.isArray(rows) ? rows.map((row) => {
    const id = String((row && (row.device_id || row.id)) || "").trim();
    if (!id) return null;
    return {
      id,
      label: String(row.display_label || row.map_label || id).trim(),
      group: String(row.notification_group || row.group || "").trim(),
      zone: String(row.zone || row.zone_id || "").trim(),
      site: String(row.site || row.site_id || "").trim(),
      online: row.is_online === true || row.online === true,
    };
  }).filter(Boolean) : [];
}

function ScopeResolver({ devices, seat, toast }) {
  const [kind, setKind] = useState("device");
  const [key, setKey] = useState("");
  const [memberId, setMemberId] = useState("");
  const options = useMemo(() => {
    if (kind === "device") return devices.map((d) => ({ key:d.id, label:d.label }));
    const seen = new Map();
    for (const d of devices) {
      const value = String(d[kind] || "").trim();
      if (value && !seen.has(value)) seen.set(value, value);
    }
    return [...seen].map(([value,label]) => ({ key:value, label }));
  }, [devices, kind]);
  const members = useMemo(() => {
    if (!key) return [];
    if (kind === "device") return devices.filter((d) => d.id === key);
    return devices.filter((d) => d[kind] === key);
  }, [devices, kind, key]);
  const chosen = memberId && members.some((d) => d.id === memberId) ? memberId : (members[0] && members[0].id) || "";
  const groupKey = kind === "group" ? key : "";

  return html`<div className="skills-v1__scope">
    <section className="skills-v1__scope-map">
      <div className="skills-v1__section-head"><div><span>Choose where it goes</span>
        <small>A zone becomes the boards in it. Nothing is invented.</small></div>
        <em>${members.length} board${members.length===1?"":"s"}</em>
      </div>
      <div className="skills-v1__scope-kinds">
        ${[["device","Board"],["group","Group"],["zone","Zone"]].map(([id,label])=>html`
          <button key=${id} type="button" data-on=${kind===id?"1":"0"}
            onClick=${()=>{setKind(id);setKey("");setMemberId("");}}>
            <${Ico} d=${id==="device"?"chip":id==="group"?"layers":"pin"} w=${15}/>
            <span><b>${label}</b><small>${id==="device"?"One board":id==="group"?"Saved membership":"Boards in this zone"}</small></span>
          </button>`)}
      </div>
      <label className="skills-v1__scope-select"><span>${kind === "device" ? "Board" : kind === "group" ? "Group" : "Zone"}</span>
        <select className="inp" value=${key} onChange=${(e)=>{setKey(e.target.value);setMemberId("");}}>
          <option value="">Choose…</option>
          ${options.map((o)=>html`<option key=${o.key} value=${o.key}>${o.label}</option>`)}
        </select>
      </label>
      <div className="skills-v1__members">
        ${members.length ? members.map((d,i)=>html`<${M.button} key=${d.id} type="button"
          data-live=${d.online?"1":"0"} data-on=${chosen===d.id?"1":"0"}
          initial=${{opacity:0,y:6}} animate=${{opacity:1,y:0}} transition=${{...SOFT,delay:i*.025}}
          onClick=${()=>setMemberId(d.id)}>
          <i/><span><b>${d.label}</b><small>${[d.group,d.zone,d.site].filter(Boolean).join(" · ") || d.id}</small></span>
        <//>`) : html`<p>Choose a board, group, or zone to see its boards.</p>`}
      </div>
    </section>

    <section className="skills-v1__resolved">
      ${seat.executeGlobal
        ? html`<${CapCampaignDesk} toast=${toast} seat=${seat}
            initialDeviceIds=${kind==="group"?"":members.map((d)=>d.id).join(", ")}
            initialGroupKey=${groupKey}/>`
        : chosen
          ? html`<${Alert} tone="info" title="This seat writes one board"
              say=${kind==="device"
                ?"This seat writes to the chosen board only."
                : `${kind==="group"?"Group":"Zone"} became ${members.length} boards. Pick one; fleet waves need a platform OTA seat.`}/>
            <${EdgeSkillsWarehouseDesk} mode="device" deviceId=${chosen} toast=${toast} seat=${seat}/>`
          : html`<div className="skills-v1__empty"><i/><b>No board chosen</b>
              <span>Choose a board, group, or zone. Empty stays empty.</span></div>`}
    </section>
  </div>`;
}

export default function SkillsPrimary({ sub, toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "");
  const caps = Array.isArray(me && me.caps) ? me.caps : [];
  const isSa = role === "superadmin" || caps.includes("sa_full_access") || caps.includes("sa_ota_admin");
  const isAdmin = isSa || role === "admin";
  const routeAt = TABS.some((tab)=>tab.id===sub) ? sub : "library";
  const [at, setAt] = useState(routeAt);
  useEffect(() => setAt(routeAt), [routeAt]);
  const active = TABS.find((tab)=>tab.id===at) || TABS[0];
  const select = (id) => {
    setAt(id);
    go("/skills/" + id);
  };
  const status = useApi(isAdmin ? "/ota/status" : "");
  const seat = { ...skillsSeatOf(status.data), baseImageAllowed:isSa };
  const register = useApi(isAdmin && at==="assign" ? path("get__devices", { query:{limit:80} }) : "");
  const devices = useMemo(()=>deviceRowsOf(register.data), [register.data]);

  if (!isAdmin) return html`<div className="sheet skills-v1 desk-craft house-rack">
    <${Alert} tone="bad" title="Skills sealed"
      say="Choosing and placing packs needs a site manager or platform seat."/>
  </div>`;

  const pane = at === "library"
    ? html`<${EdgeSkillsWarehouseDesk} mode="edge" toast=${toast} seat=${seat}/>`
    : at === "assign"
      ? html`<${ScopeResolver} devices=${devices} seat=${seat} toast=${toast}/>`
      : html`<${CapCampaignDesk} toast=${toast} seat=${seat}/>`;

  return html`<div className="sheet skills-v1 desk-craft house-rack obj-wb" data-cmc-skills-ux="skills-v1" data-cmc-obj-wb="1091">
    <div className="wrap skills-v1__wrap">
      <${WorkbenchHead}
        heading="h1"
        eyebrow="Skills · pack"
        title="Prepare packs for this fleet"
        say=${active.say}
        object=${html`<${ObjectCard} kind="Skill desk" title=${active.label}
          meta=${"Next · " + (at === "library" ? "Pick packs" : at === "assign" ? "Choose boards" : "Watch boards accept")}
          status=${{ on: at === "waves", label: at === "waves" ? "Verify" : "Prepare" }}/>`}
      />
      <${PrepSpine}
        label="Skills preparation"
        activeId=${at}
        onPick=${select}
        steps=${TABS.map((tab) => ({
          id: tab.id,
          label: tab.label,
          say: tab.verb,
          done: TABS.findIndex((t) => t.id === at) > TABS.findIndex((t) => t.id === tab.id),
        }))}
      />
      <main className="skills-v1__stage">
        <${AnimatePresence} mode="wait"><${M.div} key=${at} initial=${{opacity:0,y:10}}
          animate=${{opacity:1,y:0}} exit=${{opacity:0,y:-5}} transition=${SOFT}>${pane}<//><//>
      </main>
      <div className="obj-wb__foot">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>confirmLeaveSeat({
          fromRoute: "/skills/" + at,
          targetPath: "/machine",
          objectLabel: "Machine",
        })}>Inspect boards</button>
      </div>
    </div>
  </div>`;
}
